import { useCallback, useEffect, useRef, useState } from "react";

/**
 * BeforeAfter slider — drag to reveal the "après" image over the "avant".
 * Premium editorial styling: thin gold handle, eyebrow labels, no chrome.
 *
 * Props:
 *  - avant_url (string, required)
 *  - apres_url (string, required)
 *  - legende  (string, optional)
 *  - initial  (number, 0-100, default 50)
 *  - testid   (string, optional)
 */
export default function BeforeAfter({ avant_url, apres_url, legende, initial = 50, testid }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(initial);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPos((x / rect.width) * 100);
  }, []);

  // Pointer events (covers mouse + touch on modern browsers)
  const onPointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Keyboard accessibility
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setPos((p) => Math.max(0, p - 5));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setPos((p) => Math.min(100, p + 5));
    } else if (e.key === "Home") {
      setPos(0);
    } else if (e.key === "End") {
      setPos(100);
    }
  };

  // Sync initial prop
  useEffect(() => setPos(initial), [initial]);

  return (
    <figure className="select-none" data-testid={testid || "before-after"}>
      <div
        ref={containerRef}
        className="relative w-full aspect-[16/10] overflow-hidden bg-muted touch-none cursor-ew-resize"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* AVANT (background, full) */}
        <img
          src={avant_url}
          alt="Avant intervention"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          draggable={false}
          loading="lazy"
        />
        {/* APRES (clipped — révélée du côté gauche du curseur) */}
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
        >
          <img
            src={apres_url}
            alt="Après intervention"
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
            loading="lazy"
          />
        </div>

        {/* Eyebrow labels */}
        <span className="absolute top-4 left-4 md:top-6 md:left-6 text-[10px] md:text-[11px] tracking-[0.22em] uppercase font-medium bg-foreground/85 text-background px-3 py-1.5 pointer-events-none">
          Avant
        </span>
        <span className="absolute top-4 right-4 md:top-6 md:right-6 text-[10px] md:text-[11px] tracking-[0.22em] uppercase font-medium bg-primary text-background px-3 py-1.5 pointer-events-none">
          Après
        </span>

        {/* Handle line + grip */}
        <div
          className="absolute top-0 bottom-0 w-px bg-primary pointer-events-none"
          style={{ left: `${pos}%` }}
        />
        <button
          type="button"
          aria-label="Déplacer le curseur avant / après"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          role="slider"
          onKeyDown={onKeyDown}
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-background border border-primary/60 shadow-[0_8px_24px_-6px_rgba(0,0,0,0.35)] flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          style={{ left: `${pos}%` }}
          data-testid={`${testid || "before-after"}-handle`}
        >
          <span className="flex items-center gap-[3px] text-primary">
            <svg width="10" height="14" viewBox="0 0 10 14" fill="none" aria-hidden="true">
              <path d="M9 1L2 7L9 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <svg width="10" height="14" viewBox="0 0 10 14" fill="none" aria-hidden="true">
              <path d="M1 1L8 7L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </button>
      </div>
      {legende && (
        <figcaption className="text-serial mt-3 md:mt-4 px-1">{legende}</figcaption>
      )}
    </figure>
  );
}
